import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animationFrameScheduler, auditTime, debounceTime, distinctUntilChanged, filter, fromEvent } from 'rxjs';
import { isNotNull } from '@libs/utils';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { PatientDetails, ProviderStatus } from '@ffd/api-client';
import {
  AvatarsComponent,
  InfiniteScrollListDirective,
  InfiniteScrollListItemDirective,
  InputComponent,
  DynamicVirtualViewportHeightDirective,
} from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import { PatientSelectorService } from './patient-selector.service';

/*TODO: Move to inputs*/
const MINIMUM_SEARCH_LENGTH = 3;
const DEBOUNCE_TIME = 300;
const DEFAULT_HEIGHT = 444;

@Component({
  selector: 'lib-patient-selector',
  imports: [
    CommonModule,
    SvgIconComponent,
    InputComponent,
    LottieComponent,
    ReactiveFormsModule,
    OverlayModule,
    AvatarsComponent,
    ScrollingModule,
    DynamicVirtualViewportHeightDirective,
    TranslateModule,
    InfiniteScrollListDirective,
    InfiniteScrollListItemDirective,
    PushTagOnClickDirective,
  ],
  templateUrl: './patient-selector.component.html',
  styleUrl: './patient-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientSelectorComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(Overlay);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly patientSelectorService = inject(PatientSelectorService);

  protected patients = computed(() => {
    return this.patientSelectorService.viewModel().patients;
  });
  protected patientSignal = computed(() => this.patientSelectorService.viewModel().patient);
  protected isLoading = computed(() => this.patientSelectorService.viewModel().patientsAreLoading);

  public readonly innerControl = new FormControl<string>('');
  public readonly dropdownMaxHeight = signal(DEFAULT_HEIGHT);
  public readonly dropdownHeight = signal(DEFAULT_HEIGHT);
  public readonly prefixIconSrc = signal<SvgIcons | null>(null);
  public readonly showcaseMode = signal(false);
  public readonly dropdownOpen = signal(false);

  public readonly noResultLabelText = signal('');
  public readonly placeholderText = signal('');
  public readonly labelText = signal('');
  public readonly closeDropdownOnSelect = signal(false);
  public readonly isDisabled = signal(false);
  public readonly dropdownItems = signal<PatientDetails[] | null>(null);
  public readonly inputSize = signal<'normal' | 'large'>('normal');
  public readonly patient = computed(() => this.currentPatient() ?? this.patientSignal());
  public readonly currentPatient = signal<PatientDetails | null>(null);
  public readonly inputFocused = signal(false);

  public readonly ProviderStatus = ProviderStatus;
  public readonly TagEvent = TagEvent;
  public readonly scrollStrategy = this.overlay.scrollStrategies.noop();

  public readonly overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  public lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  @ViewChild('dropdownList', { static: false, read: ElementRef }) public dropdownElement!: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownControl', {
    static: true,
    read: ElementRef,
  })
  public dropdownControlElement!: ElementRef<HTMLDivElement>;
  @ViewChild(InputComponent) public inputComponent!: InputComponent;

  @Input({ required: true }) public searchInEmails!: boolean;

  @Input() public readonly: boolean = false;

  @Input()
  public set maxHeight(value: number) {
    this.dropdownMaxHeight.set(value);
  }

  @Input()
  public set prefixIcon(value: SvgIcons | null) {
    this.prefixIconSrc.set(value);
  }

  @Input()
  public set placeholder(value: string) {
    this.placeholderText.set(value);
  }

  @Input()
  public set label(value: string) {
    this.labelText.set(value);
  }

  @Input()
  public set isShowcaseMode(value: boolean) {
    this.showcaseMode.set(value);
  }

  @Input()
  public set disabled(value: boolean) {
    this.isDisabled.set(value);
    if (value) {
      this.innerControl.disable();
    } else {
      this.innerControl.enable();
    }
  }

  @Input()
  public set closeOnSelect(value: boolean) {
    this.closeDropdownOnSelect.set(value);
  }

  @Input()
  public set options(value: PatientDetails[] | undefined) {
    this.setDropdownHeight(this.getDropdownContainerHeight());
    this.dropdownItems.set(value ?? null);
  }

  @Input()
  public set noResultLabel(value: string) {
    this.noResultLabelText.set(value);
  }

  @Input()
  public set selectedPatient(value: PatientDetails | undefined) {
    if (value) {
      this.currentPatient.set(value);
      this.innerControl.setValue(`${value.firstName} ${value.lastName}`, { emitEvent: false });
    }
  }

  @Input() public minimumSearchLength = MINIMUM_SEARCH_LENGTH;

  @Input()
  public set size(value: 'normal' | 'large') {
    this.inputSize.set(value);
  }

  @Output() public readonly itemSelected = new EventEmitter<{ patient: PatientDetails; isInitialPatientSelect?: boolean }>();

  constructor() {
    effect(() => {
      const patient = untracked(this.patientSignal);
      const currentPatient = untracked(this.currentPatient);
      if (this.patients().length > 0) {
        this.setDropdownHeight(this.getDropdownContainerHeight());
      }
      if (patient && !currentPatient) {
        this.innerControl.setValue(`${patient?.firstName} ${patient?.lastName}`, { emitEvent: false });
        this.itemSelected.emit({ patient: patient!, isInitialPatientSelect: true });
      }
    });
  }

  public ngOnInit(): void {
    this.patientSelectorService.init();
    this.watchWindowResize();
    this.onSearchPatients(this.innerControl.value ?? '');
    this.innerControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(DEBOUNCE_TIME), filter(isNotNull), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.dropdownOpen.set(true);
        this.onSearchPatients(searchTerm);

        if (searchTerm.length < this.minimumSearchLength && this.dropdownOpen() && this.inputFocused()) {
          this.closeDropdown();
        }
      });
  }

  public ngOnDestroy(): void {
    this.patientSelectorService.clearPatientSearchTerm();
  }

  public onOverlayOutsideClick(event: MouseEvent): void {
    const clickTarget = event.target as HTMLElement;
    const isFilterCloseButtonClicked = clickTarget.closest('.dropdown-button');
    const isInputFieldClicked = this.dropdownControlElement.nativeElement.contains(clickTarget);

    if (isFilterCloseButtonClicked || isInputFieldClicked) {
      return;
    }
    event.preventDefault();
    this.onBlur();
  }

  public getDropdownContainerHeight(): number {
    const rect = this.dropdownControlElement.nativeElement.getBoundingClientRect().bottom;
    const windowHeight = window.innerHeight;
    return windowHeight - rect;
  }

  public watchWindowResize(): void {
    fromEvent(window, 'resize')
      .pipe(distinctUntilChanged(), auditTime(0, animationFrameScheduler), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.setDropdownHeight(this.getDropdownContainerHeight());
      });
  }

  public setDropdownHeight(height: number): void {
    this.dropdownHeight.set(height);
    this.cdr.markForCheck();
  }

  public openDropdown(): void {
    if (
      this.readonly ||
      this.dropdownOpen() ||
      this.isDisabled() ||
      (!this.innerControl.value && !this.dropdownItems()) ||
      (this.innerControl.value && this.innerControl.value?.length < this.minimumSearchLength)
    ) {
      return;
    }
    this.setDropdownHeight(this.getDropdownContainerHeight());
    if (!this.inputFocused()) {
      this.onSearchPatients(this.innerControl.value ?? '');
    }
    this.inputFocused.set(true);
    this.dropdownOpen.set(true);
  }

  public closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  public onFocusOut(event: FocusEvent): void {
    event.preventDefault();
    if (!this.dropdownOpen()) {
      this.onBlur();
    }
  }

  public onBlur(): void {
    this.closeDropdown();
    this.inputFocused.set(false);
    if (this.innerControl.value !== `${this.patient()?.firstName} ${this.patient()?.lastName}`) {
      this.innerControl.setValue(`${this.patient()?.firstName} ${this.patient()?.lastName}`, { emitEvent: false });
    }
  }

  public selectItem(patient: PatientDetails): void {
    this.itemSelected.emit({ patient });
    this.innerControl.setValue(`${patient.firstName} ${patient.lastName}`, { emitEvent: false });
    this.dropdownOpen.set(false);
    this.inputFocused.set(false);
    if (this.closeDropdownOnSelect()) {
      this.closeDropdown();
    }
  }

  public onEndReached(): void {
    this.onPatientsEndReached();
  }

  public clearInput(event: MouseEvent): void {
    event.stopPropagation();
    this.setDropdownHeight(this.getDropdownContainerHeight());
    this.innerControl.setValue('');
    this.onSearchPatients('');
    this.dropdownOpen.set(true);
  }

  protected onSearchPatients(searchTerm: string): void {
    this.patientSelectorService.updatePatientSearchTerm(searchTerm, this.searchInEmails);
  }

  protected onPatientsEndReached(): void {
    this.patientSelectorService.searchPatients({ searchTerm: undefined, searchInEmails: this.searchInEmails });
  }
}
